<template>
  <div 
    class="fixed bottom-0 right-0 z-50 p-4 space-y-4 pointer-events-none"
    style="max-height: 100vh; overflow-y: auto;"
  >
    <TransitionGroup
      enter-active-class="transform ease-out duration-300 transition"
      enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-for="notification in notifications"
        :key="notification.id"
        class="w-full max-w-sm overflow-hidden rounded-lg shadow-lg pointer-events-auto border"
        :class="getTypeClasses(notification.type)"
      >
        <div class="p-4">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <component 
                :is="getIcon(notification.type)"
                class="h-6 w-6"
                :class="getIconClasses(notification.type)"
              />
            </div>
            <div class="ml-3 w-0 flex-1 pt-0.5">
              <p 
                class="text-sm font-medium"
                :class="getTextClasses(notification.type)"
              >
                {{ notification.message }}
              </p>
            </div>
            <div class="ml-4 flex-shrink-0 flex">
              <button
                type="button"
                class="inline-flex rounded-md hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                :class="getTextClasses(notification.type)"
                @click="removeNotification(notification.id)"
              >
                <span class="sr-only">Close</span>
                <X class="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { useNotification } from '../composables/useNotification'
import { AlertCircle, CheckCircle, Info, X, XCircle } from 'lucide-vue-next'
import { TransitionGroup } from 'vue'

const { notifications, removeNotification } = useNotification()

const getIcon = (type: 'SUCCESS' | 'ERROR' | 'WARNING' | 'INFO') => {
  switch (type) {
    case 'SUCCESS':
      return CheckCircle
    case 'ERROR':
      return XCircle
    case 'WARNING':
      return AlertCircle
    case 'INFO':
      return Info
    default:
      return Info
  }
}

const getTypeClasses = (type: 'SUCCESS' | 'ERROR' | 'WARNING' | 'INFO') => {
  switch (type) {
    case 'SUCCESS':
      return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
    case 'ERROR':
      return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
    case 'WARNING':
      return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
    case 'INFO':
      return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
    default:
      return 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800'
  }
}

const getIconClasses = (type: 'SUCCESS' | 'ERROR' | 'WARNING' | 'INFO') => {
  switch (type) {
    case 'SUCCESS':
      return 'text-green-400'
    case 'ERROR':
      return 'text-red-400'
    case 'WARNING':
      return 'text-yellow-400'
    case 'INFO':
      return 'text-blue-400'
    default:
      return 'text-gray-400'
  }
}

const getTextClasses = (type: 'SUCCESS' | 'ERROR' | 'WARNING' | 'INFO') => {
  switch (type) {
    case 'SUCCESS':
      return 'text-green-800 dark:text-green-200'
    case 'ERROR':
      return 'text-red-800 dark:text-red-200'
    case 'WARNING':
      return 'text-yellow-800 dark:text-yellow-200'
    case 'INFO':
      return 'text-blue-800 dark:text-blue-200'
    default:
      return 'text-gray-800 dark:text-gray-200'
  }
}
</script>